import { getPinnedAwareVisibleLeafColumns } from './tableHeadUseCase';

/**
 * Column id → leaf index map (pinned-aware order).
 * @param {import('@tanstack/react-table').Column<any, unknown>[]} leaves
 * @returns {Map<string, number>}
 */
export function buildLeafColumnIdIndexMap(leaves) {
  return new Map(leaves.map((c, i) => [c.id, i]));
}

/**
 * Leaf indices spanning [columnId … columnId+colSpan) in pinned leaf order.
 * @param {number} leafCount
 * @param {Map<string, number>} columnIdToIndex
 * @param {string} columnId
 * @param {number} colSpan
 * @returns {number[]}
 */
export function leafIndicesForColumnRangeFromMap(
  leafCount,
  columnIdToIndex,
  columnId,
  colSpan,
) {
  const start = columnIdToIndex.get(columnId);
  if (start === undefined) return [];
  const span = Math.max(1, colSpan);
  /** @type {number[]} */
  const indices = [];
  for (let k = 0; k < span && start + k < leafCount; k += 1) {
    indices.push(start + k);
  }
  return indices;
}

/**
 * Leaf indices for a body cell using the **same** column order as `TableHead` / `buildTableHeadRows`
 * (`getPinnedAwareVisibleLeafColumns`). Do not use the index of the cell inside
 * `row.getVisibleCells()` — that order can differ from the pinned leaf order and causes a column
 * offset in the UI.
 * @param {import('@tanstack/react-table').Table<any>} table
 * @param {import('@tanstack/react-table').Column<any, unknown>} startColumn - First leaf column for this `<td>`
 * @param {number} colSpan
 * @returns {number[]}
 */
export function leafIndicesForTableColumnRange(table, startColumn, colSpan) {
  const leaves = getPinnedAwareVisibleLeafColumns(table);
  const idToIndex = buildLeafColumnIdIndexMap(leaves);
  return leafIndicesForColumnRangeFromMap(leaves.length, idToIndex, startColumn.id, colSpan);
}

/**
 * Whether a built thead cell's leaf range intersects hovered leaf indices.
 * @param {{ leafStartIndex: number, colspan: number }} built
 * @param {Set<number> | null | undefined} hoveredLeafIndices
 * @returns {boolean}
 */
export function builtHeadCellOverlapsHoveredLeafIndices(built, hoveredLeafIndices) {
  if (!hoveredLeafIndices?.size) return false;
  const span = Math.max(1, built.colspan);
  const end = built.leafStartIndex + span;
  for (let i = built.leafStartIndex; i < end; i += 1) {
    if (hoveredLeafIndices.has(i)) return true;
  }
  return false;
}

/**
 * For a TanStack `Header` from `table.getHeaderGroups()` (e.g. footer cells): checks whether
 * the leaf range covered by that `<th>` intersects `hoveredLeafIndices`. Prefer this with a
 * precomputed map (one `getPinnedAwareVisibleLeafColumns` per render, not per cell).
 * @param {number} leafCount
 * @param {Map<string, number>} columnIdToIndex
 * @param {import('@tanstack/react-table').Header<any, unknown>} header
 * @param {Set<number> | null | undefined} hoveredLeafIndices
 * @returns {boolean}
 */
export function footerStatHeaderOverlapsHoveredLeafIndicesFromMap(
  leafCount,
  columnIdToIndex,
  header,
  hoveredLeafIndices,
) {
  if (!hoveredLeafIndices?.size) return false;
  const start = columnIdToIndex.get(header.column.id);
  if (start === undefined) return false;
  const colDef = /** @type {TableColumnsReactTable | undefined} */ (header.column.columnDef);
  const isCheckbox = colDef?.type === 'static-checkbox';
  const span = isCheckbox ? 1 : Math.max(1, header.colSpan);
  const end = Math.min(start + span, leafCount);
  for (let i = start; i < end; i += 1) {
    if (hoveredLeafIndices.has(i)) return true;
  }
  return false;
}
